import { Loader } from 'rsuite';
import { getCompleteTimeDifference } from '@commonClient2/services/dateTime';
import i18n from '../../../services/i18n';
import DateFormatService from '../../../services/DateFormatService';
import { UkgDateFormat } from '../../../Enums';

export const Fields = Object.freeze({
    SubmissionPeriod: 'submissionPeriod',
    RequestPeriod: 'requestPeriod',
});

/**
 * @param {string} field
 * @param {Date[]} value
 */
export const roundDatePickerValues = (field, value) => {
    let [startDate, endDate] = value;
    switch (field) {
        case Fields.SubmissionPeriod:
            startDate = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                startDate.getHours(),
                Math.ceil(startDate.getMinutes() / 5) * 5,
                0,
                0,
            );
            endDate = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                endDate.getHours(),
                Math.ceil(endDate.getMinutes() / 5) * 5,
                0,
                0,
            );
            break;
        case Fields.RequestPeriod:
            startDate = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
                0,
                0,
                0,
                0,
            );
            endDate = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
                23,
                59,
                59,
                0,
            );
            break;
        default:
            break;
    }
    return [startDate, endDate];
};

/**
 * @param {string} field
 * @param {Date[]} values
 */
export const generateDateRangeData = (field, values) => {
    const [startDate, endDate] = values;
    if (field === Fields.SubmissionPeriod) {
        return {
            submissionPeriodStartDate: startDate,
            submissionPeriodEndDate: endDate,
        };
    }
    if (field === Fields.RequestPeriod) {
        return {
            requestPeriodStartDate: startDate,
            requestPeriodEndDate: endDate,
        };
    }
    return null;
};

/**
 * @param {Date} start
 * @param {Date} end
 * @returns {string} The translated difference in hours and minutes.
 * Ex.: `9 hours and 20 minutes`
 */
const getBiddingTimeDuration = (start, end) => {
    const tCommon = i18n.getFixedT(null, 'common');
    const tTimeAllowed = i18n.getFixedT(null, 'shiftBidding', 'common');

    const { hours, minutes } = getCompleteTimeDifference(start, end);
    const hoursLabel = hours ? `${hours} ${tTimeAllowed('hours')}` : '';
    const minutesLabel = minutes ? `${minutes} ${tTimeAllowed('minutes')}` : '';
    const separator = hours && minutes ? ` ${tCommon('and')} ` : '';

    return `${hoursLabel}${separator}${minutesLabel}`.toLowerCase();
};

/**
 * @param {string|Date|null} dateStart
 * @param {string|Date|null} dateEnd
 * @returns
 */
// @ts-ignore
export const isDateRangeInvalid = (dateStart, dateEnd) => !(dateStart && dateEnd && dateStart.getTime() < dateEnd.getTime());

/**
 * @param {string} prefix
 * @param {*} label
 * @returns
 */
const getInfoButtonLabel = (prefix, label) => (
    <>
        <span className="info-button-prefix">{prefix}</span>
        {label}
    </>
);

/**
 * @param {string} label
 * @param {Date} start
 * @param {Date} end
 */
export const getDuration = (label, start, end) => getInfoButtonLabel(label, getBiddingTimeDuration(start, end));

/**
 * @param {string} label
 * @param {boolean} isLoading
 * @param {Date|string|null} endDate
 */
export const getEstimatedSubmissionPeriodEndDate = (label, isLoading, endDate) => {
    const tForm = i18n.getFixedT(null, 'shiftBidding', 'form');
    return getInfoButtonLabel(
        label,
        isLoading
            ? <Loader content={tForm('schedulePeriod.submissionStartDate.loading')} />
            : DateFormatService.formatDateOrEmpty(endDate, UkgDateFormat.LongDateTime),
    );
};

/**
 * @param {number | null} d
 * @returns {boolean}
*/
export const isForever = (d) => d === 32503680000;

/**
 * @param {string|Date|null} d
 * @returns {Date|null} The `Date` when valid, `null` otherwise
 * @see https://stackoverflow.com/a/1353711
*/
export const isValidDate = (d) => {
    if (Object.prototype.toString.call(d) === '[object Date]') {
        // @ts-ignore
        // eslint-disable-next-line no-restricted-globals
        return (!isNaN(d)) ? d : null;
    }

    return null;
};
